import { useAction, useAtom } from '@reatom/npm-react'
import { memo, useMemo, useState } from 'react'
import QRCode from 'react-qr-code'
import styled from 'styled-components'

import { AsyncStateN, Redacted } from '@blade/std-lib'
import { mfaEnabledAtom } from '@root/entities/auth/atoms/reatom-user'
import {
  reatomDisableMfa,
  reatomMfaSetup,
} from '@root/features/auth/setup-otp/atoms'
import { reatomPasswordEditFlow } from '@root/features/auth/update-password/atoms/update-password'

/**
 * Turning an authenticator on, and off.
 *
 * The requests, the snackbars and the error codes are the app's own — this
 * draws them, it does not re-decide them. What it adds is the shape the board
 * uses: one step visible at a time, the key readable as text for anyone whose
 * camera will not cooperate.
 */
export const PulseOtp: React.FC<{ onDone: () => void }> = memo(({ onDone }) => {
  const [enabled] = useAtom(mfaEnabledAtom)
  // eslint-disable-next-line @reatom/unit-naming-rule
  const setup = useMemo(() => reatomMfaSetup(), [])
  // eslint-disable-next-line @reatom/unit-naming-rule
  const off = useMemo(() => reatomDisableMfa(), [])

  const create = useAction(setup.createOtp.createOtpAction)
  const [createState] = useAtom(setup.createOtp.createOtpRequestState)
  const confirm = useAction(setup.confirmOtp.execute)
  const [confirmState] = useAtom(setup.confirmOtp)
  /* The model's own `disable` opens the app's code modal; the board asks for
     the code itself, so it calls the request the same way confirm does. */
  const disable = useAction(off.execute)

  const [code, setCode] = useState('')
  const [started, setStarted] = useState(false)

  const otpUrl = AsyncStateN.matchWaiting(createState, {
    Waiting: () => undefined,
    Error: () => undefined,
    Success: (value) => value.otpUrl,
  })

  const busy = AsyncStateN.isLoading(confirmState)

  if (enabled) {
    return (
      <Form>
        <p className="lead">
          An authenticator is on. A code is asked for when you sign in from an
          address we have not seen before.
        </p>
        <label className="lbl" htmlFor="otp-off">
          Enter a current code to turn it off
        </label>
        <input
          id="otp-off"
          className="fld mono"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="000000"
          value={code}
          onChange={(event) =>
            setCode(event.target.value.replaceAll(/\D/g, ''))
          }
        />
        <button
          className="go warn"
          type="button"
          disabled={code.length < 6}
          onClick={() => {
            disable({ code })
            onDone()
          }}
        >
          Turn off two-factor
        </button>
      </Form>
    )
  }

  if (!started) {
    return (
      <Form>
        <p className="lead">
          An authenticator app makes a new six-digit code every thirty seconds.
          With it on, knowing your password is not enough to sign in as you.
        </p>
        <button
          className="go"
          type="button"
          onClick={() => {
            setStarted(true)
            create()
          }}
        >
          Set one up
        </button>
      </Form>
    )
  }

  return (
    <Form>
      <p className="lead">
        Scan this with your authenticator, then type the code it shows.
      </p>

      {otpUrl ? (
        <>
          <span className="qr">
            <QRCode size={168} value={otpUrl} />
          </span>
          <button
            className="ghost"
            type="button"
            onClick={() =>
              navigator.clipboard.writeText(otpUrl).catch(() => undefined)
            }
          >
            Copy the key instead
          </button>
        </>
      ) : (
        <span className="wait">Asking for a key…</span>
      )}

      <label className="lbl" htmlFor="otp-on">
        The code from the app
      </label>
      <input
        id="otp-on"
        className="fld mono"
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="000000"
        value={code}
        onChange={(event) => setCode(event.target.value.replaceAll(/\D/g, ''))}
      />
      <button
        className="go"
        type="button"
        disabled={code.length < 6 || busy}
        onClick={() => confirm({ code })}
      >
        {busy ? 'Checking…' : 'Turn on two-factor'}
      </button>
    </Form>
  )
})

/**
 * Changing a password, in the three steps the app's flow already has.
 *
 * The flow decides what is valid and what the server says; this only asks for
 * one thing at a time, which is the part that was worth redrawing.
 */
export const PulsePassword: React.FC<{ onDone: () => void }> = memo(
  ({ onDone }) => {
    // eslint-disable-next-line @reatom/unit-naming-rule
    const flow = useMemo(() => reatomPasswordEditFlow(), [])
    const [stage] = useAtom(flow.stageAtom)
    const [updateState] = useAtom(flow.updatePasswordStateAtom)

    const checkCurrent = useAction(flow.checkCurrentPasswordAction)
    const setNew = useAction(flow.setNewPasswordAction)
    const confirmNew = useAction(flow.confirmNewPasswordAction)

    const [value, setValue] = useState('')
    const busy = AsyncStateN.isLoading(updateState)

    const step = (() => {
      if (stage.type === 'current-password') {
        return {
          lead: 'Start with the password you use now.',
          label: 'Current password',
          cta: 'Continue',
          submit: () => checkCurrent(Redacted.make(value)),
        }
      }
      if (stage.type === 'new-password') {
        return {
          lead: 'Now the new one.',
          label: 'New password',
          cta: 'Continue',
          submit: () => setNew(Redacted.make(value)),
        }
      }
      return {
        lead: 'Once more, to be sure it is what you meant.',
        label: 'Repeat the new password',
        cta: 'Change password',
        submit: () => {
          confirmNew(Redacted.make(value))
          onDone()
        },
      }
    })()

    return (
      <Form>
        <p className="lead">{step.lead}</p>
        <label className="lbl" htmlFor="pw">
          {step.label}
        </label>
        <input
          id="pw"
          className="fld"
          type="password"
          autoComplete={
            stage.type === 'current-password'
              ? 'current-password'
              : 'new-password'
          }
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
        <button
          className="go"
          type="button"
          disabled={value.length === 0 || busy}
          onClick={() => {
            step.submit()
            setValue('')
          }}
        >
          {busy ? 'Saving…' : step.cta}
        </button>
      </Form>
    )
  },
)

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 6px 6px;

  .lead {
    margin: 0;
    font-size: 13px;
    line-height: 20px;
    color: var(--t3);
  }

  .qr {
    align-self: center;
    padding: 12px;
    border-radius: 10px;
    background: #ffffff;
    line-height: 0;
  }

  .wait {
    align-self: center;
    padding: 28px 0;
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 11px;
    color: var(--t4);
  }

  .lbl {
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: var(--t4);
  }

  .fld {
    height: 44px;
    box-sizing: border-box;
    padding: 0 12px;
    background: #0d0e12;
    border: 1px solid var(--line);
    border-radius: 5px;
    color: var(--t1);
    font-family: inherit;
    font-size: 14px;
    outline: none;
  }

  .fld:focus {
    border-color: var(--accent);
  }

  .fld.mono {
    font-family: 'Geist Mono', ui-monospace, monospace;
    letter-spacing: 3px;
  }

  .go {
    height: 44px;
    border: 0;
    border-radius: 5px;
    background: var(--t1);
    color: var(--bg);
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
  }

  .go:disabled {
    opacity: 0.4;
    cursor: default;
  }

  .go.warn {
    background: var(--down);
    color: var(--t1);
  }

  .ghost {
    align-self: center;
    height: 30px;
    padding: 0 12px;
    border: 1px solid var(--chip-hi);
    border-radius: 5px;
    background: var(--chip);
    color: var(--t3);
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
  }
`
